import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type HorizontalScrollerProps = {
  children: ReactNode;
};

const FADE_WIDTH = '56px';

// Keeps the filter chips on a single row: overflowing content scrolls
// horizontally, the scrollable edge(s) fade out via a mask, and arrow
// affordances fade in wherever there is more to scroll.
export const HorizontalScroller = ({ children }: HorizontalScrollerProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (el == null) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (el == null || content == null) {
      return;
    }
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    // Watch the viewport for resize and the content for chips
    // appearing/disappearing — both change what's scrollable.
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    resizeObserver.observe(content);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  const scrollByStep = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (el == null) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  const leftStops = canScrollLeft
    ? `transparent 0, #000 ${FADE_WIDTH}`
    : `#000 0, #000 ${FADE_WIDTH}`;
  const rightStops = canScrollRight
    ? `#000 calc(100% - ${FADE_WIDTH}), transparent 100%`
    : `#000 calc(100% - ${FADE_WIDTH}), #000 100%`;
  const maskImage = `linear-gradient(to right, ${leftStops}, ${rightStops})`;

  return (
    <Box sx={{ position: 'relative', flex: 1, mx: 1, minWidth: 0 }}>
      <ScrollAffordance
        side="left"
        visible={canScrollLeft}
        onClick={() => scrollByStep(-1)}
      />
      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          maskImage,
          WebkitMaskImage: maskImage,
          transition: 'mask-image 0.2s ease, -webkit-mask-image 0.2s ease',
        }}
      >
        <Box
          ref={contentRef}
          sx={{ display: 'inline-flex', gap: 1, alignItems: 'flex-start' }}
        >
          {children}
        </Box>
      </Box>
      <ScrollAffordance
        side="right"
        visible={canScrollRight}
        onClick={() => scrollByStep(1)}
      />
    </Box>
  );
};

type ScrollAffordanceProps = {
  side: 'left' | 'right';
  visible: boolean;
  onClick: () => void;
};

const ScrollAffordance = ({
  side,
  visible,
  onClick,
}: ScrollAffordanceProps) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      [side]: 0,
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'auto' : 'none',
      transition: 'opacity 0.2s ease',
    }}
  >
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        bgcolor: 'white.main',
        color: 'text.secondary',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1,
        '&:hover': { bgcolor: 'white.main' },
      }}
    >
      {side === 'left' ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
    </IconButton>
  </Box>
);
